import React, { useEffect, useState } from 'react';
import { Globe, RefreshCw, CheckCircle2, XCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { EngineCard } from './EngineCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso) => (iso ? new Date(iso).toLocaleString('nl-BE', { dateStyle: 'medium', timeStyle: 'short' }) : null);

const IndexPanel = () => {
  const [report, setReport] = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(API_URL + '/api/seo/index-status')
      .then((r) => (r.ok ? r.json() : {}))
      .then((d) => d && d.ran_at && setReport(d))
      .catch(() => {});
  }, []);

  const runAll = async () => {
    setRunning(true);
    setError('');
    try {
      const res = await fetch(API_URL + '/api/seo/index-all', { method: 'POST' });
      if (!res.ok) throw new Error((await res.json()).detail || 'Indexeren mislukt');
      setReport(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="index-panel">
      <div className="bg-white rounded-xl border p-6 flex flex-col md:flex-row md:items-center gap-4 justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[#3a190b] flex items-center gap-2"><Globe className="h-5 w-5" /> Alles indexeren</h2>
          <p className="text-sm text-gray-600 mt-1">Vernieuwt de sitemap (blogs + projecten), pusht elke pagina naar Bing/IndexNow (de index achter ChatGPT zoeken) en controleert live of Google, Claude en andere AI-crawlers alles kunnen lezen.</p>
          {report?.ran_at && <p className="text-xs text-gray-400 mt-2" data-testid="index-last-run">Laatste run: {formatDate(report.ran_at)} · {report.urls_total} URL's ({report.sources?.pages} pagina's, {report.sources?.blogs} blogs, {report.sources?.projects} projecten)</p>}
        </div>
        <Button onClick={runAll} disabled={running} className="bg-[#3a190b] hover:bg-[#2a1208] text-white rounded-full px-6 shrink-0" data-testid="index-all-button">
          <RefreshCw className={`h-4 w-4 mr-2 ${running ? 'animate-spin' : ''}`} /> {running ? 'Bezig met indexeren…' : 'Alles indexeren'}
        </Button>
      </div>

      {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="index-error">{error}</p>}

      {report?.engines && (
        <div className="grid md:grid-cols-3 gap-4" data-testid="index-engines">
          {report.engines.map((e) => <EngineCard key={e.id} engine={e} />)}
        </div>
      )}

      {report?.checks && (
        <div className="bg-white rounded-xl border p-5" data-testid="index-checks">
          <h3 className="font-semibold text-[#3a190b] mb-3">Live controle op maxq.be</h3>
          <ul className="divide-y">
            {report.checks.map((c) => (
              <li key={c.url} className="py-2 flex items-center justify-between gap-3 text-sm">
                <span className="flex items-center gap-2">
                  {c.ok ? <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" /> : <XCircle className="h-4 w-4 text-red-600 shrink-0" />}
                  {c.label}
                </span>
                <a href={c.url} target="_blank" rel="noreferrer" className="text-xs text-gray-400 font-mono truncate max-w-[50%] hover:text-[#3a190b]">{c.url.replace('https://maxq.be', '')} · {c.status}</a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default IndexPanel;
